import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles as SparklesIcon, Truck as TruckIcon, TrendingUp as TrendingUpIcon, ShieldCheck as ShieldCheckIcon, Zap as ZapIcon } from "lucide-react";

/* Smart allocation, sold as an engine: a live AI core, three benefit cards (why it's
   worth turning on), an ambient blue aurora, and a payoff line. When off it stays
   inviting — never punishes choosing the courier by hand. Fully mobile-safe (stacks,
   no horizontal overflow). Behaviour is unchanged; this is presentation only. */
const ALLOC_BENEFITS = [
  { Icon: TrendingUpIcon,  title: "Higher delivery rates", desc: "Every order routes to the courier that performs best on its lane." },
  { Icon: ShieldCheckIcon, title: "Lower RTO losses",      desc: "Fewer failed deliveries, and less revenue lost to returns." },
  { Icon: ZapIcon,         title: "Effortless at scale",   desc: "Optimised the moment you book — at ten orders or ten thousand." },
];

function AllocAurora() {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      <motion.span className="absolute -left-16 -top-16 h-44 w-44 rounded-full bg-[#3877FC]/12 blur-3xl"
        animate={{ x: [0, 20, 0], y: [0, 12, 0] }} transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }} />
      <motion.span className="absolute -bottom-20 right-2 h-40 w-40 rounded-full bg-[#38BDF8]/10 blur-3xl"
        animate={{ x: [0, -14, 0], y: [0, -10, 0] }} transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }} />
    </div>
  );
}

function AllocModeCard({ active, onSelect, Icon, title, desc, isSmart }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={active}
      onClick={onSelect}
      className={`group relative flex items-start gap-3 rounded-xl border p-3.5 text-left transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#3877FC]/30 ${
        active
          ? "border-[#3877FC] bg-[#3877FC]/[0.045] shadow-[0_2px_12px_-5px_rgba(56,119,252,0.45)]"
          : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50/60"
      }`}
    >
      <span className={`mt-0.5 grid h-[18px] w-[18px] flex-shrink-0 place-items-center rounded-full border-2 transition-colors ${active ? "border-[#3877FC]" : "border-slate-300 group-hover:border-slate-400"}`}>
        <AnimatePresence>{active && (
          <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }} transition={{ type: "spring", stiffness: 500, damping: 30 }} className="h-2 w-2 rounded-full bg-[#3877FC]" />
        )}</AnimatePresence>
      </span>
      <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-lg transition-all"
        style={active ? { background: "linear-gradient(135deg,#3877FC,#4F6BF6)", boxShadow: "0 5px 14px -4px rgba(56,119,252,0.5)" } : { background: "#EEF2F8" }}>
        <Icon className={`h-[18px] w-[18px] ${active ? "text-white" : "text-slate-400"}`} />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className={`text-[14px] font-bold ${active ? "text-slate-900" : "text-slate-700"}`}>{title}</span>
          {isSmart && <span className="cfo-ai-badge inline-flex items-center px-[7px] py-[3px] text-[9px] font-extrabold uppercase tracking-[0.09em]"><span className="cfo-ai-badge-text">AI</span></span>}
        </span>
        <span className="mt-0.5 block text-[12px] leading-relaxed text-slate-500">{desc}</span>
      </span>
    </button>
  );
}

export default function SmartAllocationEngine({ on, onToggle }) {
  const selectSmart = () => { if (!on) onToggle(); };
  const selectManual = () => { if (on) onToggle(); };
  return (
    <div className="relative overflow-hidden rounded-2xl border border-[#E2EAF8] bg-gradient-to-b from-white to-[#FAFBFF]">
      <AnimatePresence>{on && (
        <motion.div key="aurora" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.5 }}>
          <AllocAurora />
        </motion.div>
      )}</AnimatePresence>

      <div className="relative p-4 sm:p-5">
        {/* Selection mode — two explicit, discoverable choices */}
        <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500">Selection mode</p>
        <div className="mt-2.5 grid grid-cols-1 gap-3 sm:grid-cols-2" role="radiogroup" aria-label="Courier selection mode">
          <AllocModeCard active={on} onSelect={selectSmart} Icon={SparklesIcon} isSmart title="Smart allocation" desc="Best courier chosen automatically at booking." />
          <AllocModeCard active={!on} onSelect={selectManual} Icon={TruckIcon} title="Manual selection" desc="Choose a specific courier from the live rates." />
        </div>

        {/* Why smart is worth it — benefit cards + payoff (only when smart is selected) */}
        <AnimatePresence initial={false}>
          {on && (
            <motion.div
              key="benefits"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ height: { type: "spring", stiffness: 260, damping: 30 }, opacity: { duration: 0.2 } }}
              className="overflow-hidden"
            >
              <div className="mt-4 border-t border-slate-100 pt-4">
                <p className="text-[11px] font-bold uppercase tracking-[0.1em] text-[#3877FC]">What you get</p>
                <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  {ALLOC_BENEFITS.map((b, i) => (
                    <motion.div
                      key={b.title}
                      className="relative overflow-hidden rounded-xl p-[1.25px]"
                      style={{ background: "linear-gradient(135deg,#3877FC,#7CA0FA)" }}
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: 0.1 + i * 0.09, type: "spring", stiffness: 300, damping: 26 }}
                    >
                      <div className="h-full rounded-[10.75px] bg-white p-3.5">
                        <span className="grid h-9 w-9 place-items-center rounded-lg text-white"
                          style={{ background: "linear-gradient(135deg,#3877FC,#4F6BF6)", boxShadow: "0 5px 14px -4px rgba(56,119,252,0.55)" }}>
                          <b.Icon className="h-[18px] w-[18px]" />
                        </span>
                        <p className="mt-2.5 text-[13px] font-bold text-slate-900">{b.title}</p>
                        <p className="mt-1 text-[12px] leading-relaxed text-slate-500">{b.desc}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                <motion.div
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}
                  className="mt-3 flex items-center gap-2.5 rounded-xl bg-[#3877FC]/[0.06] px-4 py-2.5 ring-1 ring-inset ring-[#3877FC]/10"
                >
                  <SparklesIcon className="h-4 w-4 flex-shrink-0 text-[#3877FC]" />
                  <p className="text-[12.5px] leading-relaxed text-slate-600">More delivered, fewer returned, nothing to manage — on <b className="font-semibold text-slate-800">every order</b> you ship.</p>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
